<!--
  The saved Concrete Field Testing logs.

  A list and a "New field test log" button, nothing else: the sheet itself is
  the form on the next screen (Task 5). Follows MixDesignList.svelte's
  structure.

  Per-site: with `?site=`, this is one site's logs, newest first — the
  grouped view lives on the site page (SiteForm.svelte) now, this screen is
  just its "All field test logs" drill-down. Without a site, every log
  across every site, flat, newest first.
-->
<script lang="ts">
  import { linkHandler, location, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { fieldTestLogs, fieldTestLogsLoading, loadFieldTestLogs } from '../../lib/store';

  const siteId = $derived(siteIdFromQuery($location.query));

  $effect(() => {
    void loadFieldTestLogs(siteId);
  });

  /** '2026-08-21T09:14:02.101Z' -> '2026-08-21'. */
  function day(timestamp: string): string {
    return timestamp.slice(0, 10);
  }

  /** A log has no date of its own; newest by when it was last touched. */
  const sorted = $derived([...$fieldTestLogs].sort((a, b) => b.updated_at.localeCompare(a.updated_at)));
</script>

<div class="card">
  <div class="card__body">
    {#if siteId !== null}
      <a class="btn btn--sm" href={routePath.qualitySite(siteId)} onclick={linkHandler}>&#8592; Back to site</a>
    {:else}
      <a class="btn btn--sm" href={routePath.quality()} onclick={linkHandler}>&#8592; All sites</a>
    {/if}
    <h1 class="card__title">Concrete Field Testing</h1>
    <button
      class="btn btn--primary btn--block"
      type="button"
      onclick={() => navigate(routePath.newQualityFieldTest(siteId ?? undefined))}
    >
      New field test log
    </button>
  </div>
</div>

{#if $fieldTestLogsLoading && $fieldTestLogs.length === 0}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sorted.length === 0}
  <div class="card">
    <div class="card__body">
      <p class="meta">
        No field test logs saved yet. The first one starts from the standard SCI sheet.
      </p>
    </div>
  </div>
{:else}
  <div class="card">
    <div class="card__body">
      <ul class="qlist">
        {#each sorted as log (log.id)}
          <li>
            <a href={routePath.qualityFieldTest(log.id)} onclick={linkHandler}>
              <strong>{log.title || 'Untitled log'}</strong>
              <span class="meta">
                {log.row_count} row{log.row_count === 1 ? '' : 's'} · updated {day(
                  log.updated_at
                )}
              </span>
            </a>
          </li>
        {/each}
      </ul>
    </div>
  </div>
{/if}

<style>
  .qlist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .qlist li + li {
    border-top: 1px solid var(--line-soft);
  }

  .qlist a {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.85rem 0;
    color: inherit;
    text-decoration: none;
    /* Tapped with gloves on, same as the field screens. */
    min-height: var(--tap);
  }

  .qlist a:hover strong {
    text-decoration: underline;
  }
</style>
